import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  IconButton,
  Button,
  Avatar,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  Share,
  LocationOn,
  Business,
  Work,
  Schedule,
  AttachMoney,
  Star,
  Visibility,
  HomeWork,
  AccessTime,
  TrendingUp,
  Check,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import JobApplicationModal from '../JobApplication/JobApplicationModal';
import { useUIStore } from '../../store/uiStore';

interface JobCardProps {
  job: {
    id: string;
    title: string;
    company: string;
    companyLogo?: string;
    location: string;
    salary?: {
      min: number;
      max: number;
      currency: string;
    };
    employmentType: string; // 'full-time', 'part-time', 'contract', 'internship'
    experienceLevel: string; // 'entry', 'mid', 'senior', 'executive'
    isRemote: boolean;
    postedDate: string;
    description?: string;
    requirements?: string[];
    benefits?: string[];
    tags?: string[];
    isNew?: boolean;
    isFeatured?: boolean;
    isUrgent?: boolean;
    applicantsCount?: number;
    viewsCount?: number;
    duration?: string; // 'Contract to hire', 'Project-based', etc.
    sellerId?: string; // ID of the user who posted the job
  };
  onApply?: (jobId: string) => void;
  onToggleFavorite?: (jobId: string) => void;
  onViewDetails?: (jobId: string) => void;
  onShare?: (jobId: string) => void;
  isFavorite?: boolean;
  isOwnJob?: boolean;
  variant?: 'default' | 'compact' | 'detailed';
}

const JobCard: React.FC<JobCardProps> = ({
  job,
  onApply,
  onToggleFavorite,
  onViewDetails,
  onShare,
  isFavorite = false,
  isOwnJob = false,
  variant = 'default',
}) => {
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const { 
    jobApplicationWriterMinimized, 
    setJobApplicationWriterOpen, 
    addSelectedJob, 
    removeSelectedJob, 
    isJobSelected 
  } = useUIStore();

  const handleApply = (e: React.MouseEvent) => {
    e.stopPropagation();
    setApplicationModalOpen(true);
  };

  const handleChoose = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Add job to selected jobs and open the Job Application Writer modal
    console.log('JobCard handleChoose - adding job.id:', job.id, 'job object:', job);
    addSelectedJob(job.id, job);
    setJobApplicationWriterOpen(true);
  };

  const handleToggleChoose = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('JobCard handleToggleChoose - job.id:', job.id, 'isJobSelected:', isJobSelected(job.id));
    if (isJobSelected(job.id)) {
      removeSelectedJob(job.id);
    } else {
      addSelectedJob(job.id, job);
    }
  };

  const handleApplicationSubmit = (applicationData: any) => {
    // Handle application submission
    console.log('Application submitted:', applicationData);
    onApply?.(job.id);
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.(job.id);
  };

  const handleViewDetails = () => {
    onViewDetails?.(job.id);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(job.id);
  };

  const getEmploymentTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'full-time':
        return '#10b981'; // green
      case 'part-time':
        return '#3b82f6'; // blue
      case 'contract':
        return '#f59e0b'; // amber
      case 'internship':
        return '#8b5cf6'; // purple
      default:
        return '#6b7280'; // gray
    }
  };

  const getExperienceLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'entry':
        return '#10b981'; // green
      case 'mid':
        return '#3b82f6'; // blue
      case 'senior':
        return '#f59e0b'; // amber
      case 'executive':
        return '#ef4444'; // red
      default:
        return '#6b7280'; // gray
    }
  };

  const formatSalary = () => {
    if (!job.salary) return null;
    const { min, max, currency } = job.salary;
    return `${currency}${min.toLocaleString()} - ${currency}${max.toLocaleString()}`;
  };

  const companyName = (() => {
    const raw = String(job.company || '').trim();
    if (!raw) return 'Company';
    if (raw.toLowerCase() === 'company name') return 'Company';
    if (raw.toLowerCase() === 'unknown company') return 'Company';
    return raw;
  })();

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return `${Math.floor(diffInHours / 168)}w ago`;
    }
  };

  if (variant === 'compact') {
    return (
      <motion.div
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.99 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        <Card
          sx={{
            display: 'flex',
            cursor: 'pointer',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              borderColor: '#3b82f6',
            },
            transition: 'all 0.2s ease',
            p: 2,
            mb: 1,
            minHeight: 200, // Increased height for compact variant
          }}
          onClick={handleViewDetails}
        >

          {/* Job Info */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* Job Title */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.1rem',
                lineHeight: 1.3,
                mb: 0.5,
                color: '#1f2937',
                cursor: 'pointer',
                '&:hover': {
                  color: '#3b82f6',
                },
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {job.title}
            </Typography>

            {/* Beautiful Company Container */}
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.5,
              bgcolor: '#eff6ff',
              borderRadius: 1.5,
              border: '1px solid #dbeafe',
              mb: 1,
              alignSelf: 'flex-start'
            }}>
              <Business sx={{ fontSize: 14, color: '#3b82f6' }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#1e40af', 
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}
              >
                {job.company}
              </Typography>
            </Box>

            {/* Location and Remote */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LocationOn sx={{ fontSize: 16, color: '#6b7280' }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {job.isRemote ? 'Remote' : (job.location || 'Location not specified')}
              </Typography>
              {job.isRemote && (
                <HomeWork sx={{ fontSize: 16, color: '#10b981' }} />
              )}
            </Box>

            {/* Employment Type and Experience */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Chip
                label={job.employmentType}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  height: 20,
                  bgcolor: getEmploymentTypeColor(job.employmentType),
                  color: 'white',
                  fontWeight: 500,
                }}
              />
              <Chip
                label={job.experienceLevel}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  height: 20,
                  bgcolor: getExperienceLevelColor(job.experienceLevel),
                  color: 'white',
                  fontWeight: 500,
                }}
              />
            </Box>

            {/* Salary */}
            {job.salary && (
              <Typography variant="body2" sx={{ color: '#059669', fontWeight: 600, mb: 1 }}>
                {formatSalary()}
              </Typography>
            )}

            {/* Posted Date */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              Posted {getTimeAgo(job.postedDate)}
            </Typography>

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleApply}
                sx={{
                  bgcolor: '#3b82f6',
                  color: 'white',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 2,
                  py: 0.5,
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  '&:hover': {
                    bgcolor: '#2563eb',
                    transform: 'translateY(-1px)',
                  },
                  boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                  transition: 'all 0.2s ease',
                }}
              >
                Apply Now
              </Button>
              
              <IconButton 
                onClick={handleToggleFavorite} 
                sx={{
                  color: isFavorite ? '#ef4444' : '#6b7280',
                  '&:hover': {
                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                  }
                }}
                size="small"
              >
                {isFavorite ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
              </IconButton>

              <IconButton 
                onClick={handleShare}
                sx={{
                  color: '#6b7280',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                  }
                }}
                size="small"
              >
                <Share fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <Card
        sx={{
          height: '100%', // Use full height of grid container
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          borderRadius: 2,
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e5e7eb',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
            borderColor: '#3b82f6',
          },
          transition: 'all 0.3s ease',
        }}
        onClick={handleViewDetails}
      >
        {/* Header with Job Title and Badges */}
        <Box sx={{ 
          p: 2, 
          pb: 1, 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'space-between',
          bgcolor: '#f8fafc',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: '#1f2937', 
                mb: 1,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.3
              }}
            >
              {job.title}
            </Typography>
            
            {/* Beautiful Company Container */}
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              bgcolor: '#eff6ff',
              borderRadius: 2,
              border: '1px solid #dbeafe',
              mb: 1
            }}>
              {job.companyLogo ? (
                <Avatar
                  src={job.companyLogo}
                  alt={companyName}
                  sx={{ width: 20, height: 20, bgcolor: 'white', border: '1px solid #dbeafe' }}
                  imgProps={{ referrerPolicy: 'no-referrer' }}
                />
              ) : (
                <Business sx={{ fontSize: 16, color: '#3b82f6' }} />
              )}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#1e40af', 
                  fontWeight: 600,
                  fontSize: '0.85rem'
                }}
              >
                {companyName}
              </Typography>
            </Box>
          </Box>

          {/* Badges + Actions (kept together so they never overlap) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end', ml: 2, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
              {job.isNew && (
                <Chip
                  label="NEW"
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    height: 20,
                    bgcolor: '#10b981',
                    color: 'white',
                  }}
                />
              )}
              {job.isFeatured && (
                <Chip
                  label="FEATURED"
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    height: 20,
                    bgcolor: '#f59e0b',
                    color: 'white',
                  }}
                />
              )}
              {job.isUrgent && (
                <Chip
                  label="URGENT"
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    height: 20,
                    bgcolor: '#ef4444',
                    color: 'white',
                  }}
                />
              )}
              {isOwnJob && (
                <Chip
                  label="MY JOB"
                  size="small"
                  sx={{
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    height: 20,
                    bgcolor: '#8b5cf6',
                    color: 'white',
                  }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
                <IconButton
                  onClick={handleToggleFavorite}
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: isFavorite ? 'error.main' : 'common.white',
                    color: isFavorite ? 'common.white' : '#6b7280',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    '&:hover': {
                      bgcolor: isFavorite ? 'error.dark' : '#f9fafb',
                    }
                  }}
                >
                  {isFavorite ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Share">
                <IconButton
                  onClick={handleShare}
                  size="small"
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: 'common.white',
                    color: '#6b7280',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    '&:hover': {
                      bgcolor: '#f9fafb',
                    }
                  }}
                >
                  <Share fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Job Content */}
        <CardContent sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Location and Remote */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocationOn sx={{ fontSize: 18, color: '#6b7280' }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {job.isRemote ? 'Remote' : (job.location || 'Location not specified')}
            </Typography>
            {job.isRemote && (
              <HomeWork sx={{ fontSize: 18, color: '#10b981', ml: 1 }} />
            )}
          </Box>

          {/* Employment Type and Experience Level */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Chip
              label={job.employmentType}
              size="small"
              sx={{
                fontSize: '0.75rem',
                height: 24,
                bgcolor: getEmploymentTypeColor(job.employmentType),
                color: 'white',
                fontWeight: 500,
              }}
            />
            <Chip
              label={job.experienceLevel}
              size="small"
              sx={{
                fontSize: '0.75rem',
                height: 24,
                bgcolor: getExperienceLevelColor(job.experienceLevel),
                color: 'white',
                fontWeight: 500,
              }}
            />
            {job.duration && (
              <Chip
                label={job.duration}
                size="small"
                sx={{
                  fontSize: '0.75rem',
                  height: 24,
                  bgcolor: '#6366f1',
                  color: 'white',
                  fontWeight: 500,
                }}
              />
            )}
          </Box>

          {/* Salary */}
          {job.salary && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AttachMoney sx={{ fontSize: 18, color: '#059669' }} />
              <Typography variant="body1" sx={{ color: '#059669', fontWeight: 600 }}>
                {formatSalary()}
              </Typography>
            </Box>
          )}

          {/* Required Skills */}
          {job.requirements && job.requirements.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, mb: 1, display: 'block' }}>
                Required Skills:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 60, overflow: 'hidden' }}>
                {job.requirements.slice(0, 3).map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: 22,
                      borderColor: '#3b82f6',
                      color: '#3b82f6',
                      bgcolor: '#eff6ff',
                      '&:hover': {
                        borderColor: '#2563eb',
                        color: '#2563eb',
                      }
                    }}
                  />
                ))}
                {job.requirements.length > 3 && (
                  <Chip
                    label={`+${job.requirements.length - 3} more`}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: 22,
                      borderColor: '#d1d5db',
                      color: '#6b7280',
                    }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Benefits/Perks */}
          {job.benefits && job.benefits.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, mb: 1, display: 'block' }}>
                Benefits:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxHeight: 60, overflow: 'hidden' }}>
                {job.benefits.slice(0, 2).map((benefit, index) => (
                  <Chip
                    key={index}
                    label={benefit}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: 22,
                      borderColor: '#10b981',
                      color: '#10b981',
                      bgcolor: '#f0fdf4',
                      '&:hover': {
                        borderColor: '#059669',
                        color: '#059669',
                      }
                    }}
                  />
                ))}
                {job.benefits.length > 2 && (
                  <Chip
                    label={`+${job.benefits.length - 2} more`}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: 22,
                      borderColor: '#d1d5db',
                      color: '#6b7280',
                    }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Stats */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            {job.applicantsCount && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TrendingUp sx={{ fontSize: 16, color: '#6b7280' }} />
                <Typography variant="caption" color="text.secondary">
                  {job.applicantsCount} applicants
                </Typography>
              </Box>
            )}
            {job.viewsCount && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Visibility sx={{ fontSize: 16, color: '#6b7280' }} />
                <Typography variant="caption" color="text.secondary">
                  {job.viewsCount} views
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16, color: '#6b7280' }} />
              <Typography variant="caption" color="text.secondary">
                {getTimeAgo(job.postedDate)}
              </Typography>
            </Box>
          </Box>
        </CardContent>

        {/* Actions */}
        <CardActions sx={{ 
          p: 2, 
          pt: 0, 
          flexDirection: 'column', 
          gap: 1,
          mt: 'auto',
          bgcolor: '#f8fafc',
          borderTop: '1px solid #e5e7eb'
        }}>
          <Button
            variant="contained"
            fullWidth
            onClick={jobApplicationWriterMinimized ? handleToggleChoose : handleApply}
            startIcon={jobApplicationWriterMinimized && isJobSelected(job.id) ? <Check /> : undefined}
            sx={{
              bgcolor: jobApplicationWriterMinimized 
                ? (isJobSelected(job.id) ? '#10b981' : '#8b5cf6')
                : '#3b82f6',
              color: 'white',
              fontWeight: 600,
              borderRadius: 2,
              py: 1.5,
              textTransform: 'none',
              fontSize: '0.9rem',
              '&:hover': {
                bgcolor: jobApplicationWriterMinimized 
                  ? (isJobSelected(job.id) ? '#059669' : '#7c3aed')
                  : '#2563eb',
                transform: 'translateY(-1px)',
                boxShadow: jobApplicationWriterMinimized 
                  ? (isJobSelected(job.id) 
                    ? '0 4px 12px rgba(16, 185, 129, 0.4)'
                    : '0 4px 12px rgba(139, 92, 246, 0.4)')
                  : '0 4px 12px rgba(59, 130, 246, 0.4)',
              },
              boxShadow: jobApplicationWriterMinimized 
                ? (isJobSelected(job.id) 
                  ? '0 2px 4px rgba(16, 185, 129, 0.3)'
                  : '0 2px 4px rgba(139, 92, 246, 0.3)')
                : '0 2px 4px rgba(59, 130, 246, 0.3)',
              transition: 'all 0.2s ease',
            }}
          >
            {jobApplicationWriterMinimized 
              ? (isJobSelected(job.id) ? 'Chosen' : 'Choose')
              : 'Apply Now'
            }
          </Button>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Visibility />}
            onClick={handleViewDetails}
            sx={{
              borderColor: '#d1d5db',
              color: '#374151',
              textTransform: 'none',
              fontSize: '0.9rem',
              '&:hover': {
                borderColor: '#3b82f6',
                bgcolor: '#f0f9ff',
                color: '#3b82f6',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            View Details
          </Button>
        </CardActions>

      </Card>

      {/* Application Modal */}
      <JobApplicationModal
        open={applicationModalOpen}
        onClose={() => setApplicationModalOpen(false)}
        jobTitle={job.title}
        companyName={job.company}
        productId={job.id}
        sellerId={job.sellerId || ''}
        onApply={handleApplicationSubmit}
      />
    </motion.div>
  );
};

export default JobCard;
